/// <reference types="node" />
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/components/auth/AuthContext';
import Card from '@shared/Card';
import Button from '@shared/Button';
import QuotaCard from '@shared/QuotaCard';
import {FileText, MessageSquare, Users, TrendingUp, RefreshCw} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api'; // Import the API utility
import LoadingSpinner from '@shared/LoadingSpinner'; // Assuming this component exists
import ToggleSwitch from '@shared/ToggleSwitch';
import SystemHealthModal from "@/components/settings/SystemHealthModal"; // Import ToggleSwitch component
import type { LucideIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface DashboardStats {
  totalDocuments: string;
  queriesThisMonth: string;
  activeUsers: string;
  avgResponseTime: string;
}

interface Activity {
  id: string;
  query: string;
  user: string;
  timestamp: string;
  status: string;
}

interface QuickAction {
  label: string;
  onClick: () => void;
  variant: 'primary' | 'secondary' | 'success' | 'chat';
}

interface Stat {
  id: number;
  name: string;
  stat: React.ReactNode;
  icon: LucideIcon;
  color: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State for dashboard statistics
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalDocuments: '...',
    queriesThisMonth: '...',
    activeUsers: '...',
    avgResponseTime: '...'
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [errorStats, setErrorStats] = useState<string | null>(null);
  const [isHealthModalOpen, setIsHealthModalOpen] = useState(false);

  // State for recent activity
  const [recentActivity, setRecentActivity] = useState<Activity[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [errorActivity, setErrorActivity] = useState<string | null>(null);

  // State for auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  // State for quota data
  const [quotaData, setQuotaData] = useState<any>(null);
  const [loadingQuota, setLoadingQuota] = useState(true);

  // Combined data fetching function for dashboard stats and recent activity
  const fetchDashboardData = useCallback(async () => {
    setLoadingStats(true);
    setLoadingActivity(true);
    setErrorStats(null);
    setErrorActivity(null);

    try {
      // Fetch Dashboard Statistics
      const statsData = await api.getAnalyticsOverview();
      let formattedAvgResponseTime = 'N/A';
      if (statsData.avgResponseTime !== undefined && statsData.avgResponseTime !== null) {
        const responseTimeMs = parseFloat(statsData.avgResponseTime);
        if (!isNaN(responseTimeMs)) {
          formattedAvgResponseTime = responseTimeMs < 1000 ? `${Math.round(responseTimeMs)}ms`: `${(responseTimeMs / 1000).toFixed(1)}s`;
        }
      }
      setDashboardStats({
        totalDocuments: statsData.totalDocuments?.toLocaleString() || 'N/A',
        queriesThisMonth: statsData.totalQueries?.toLocaleString() || 'N/A',
        activeUsers: statsData.activeUsers?.toLocaleString() || 'N/A',
        avgResponseTime: formattedAvgResponseTime
      });
    } catch (error: any) {
      toast.error(`Failed to load dashboard statistics: ${error?.message || 'Unknown error'}`);
      console.error('Failed to fetch dashboard statistics:', error);
      setErrorStats('Failed to load dashboard statistics. Please try again later.');
      setDashboardStats({
        totalDocuments: 'Error',
        queriesThisMonth: 'Error',
        activeUsers: 'Error',
        avgResponseTime: 'Error'
      });
    } finally {
      setLoadingStats(false);
    }

    try {
      // Fetch Recent Activity
      const activityData = await api.getUserActivity('7d'); // Using '7d' for recent activity
      if (activityData && activityData.topUsers && activityData.topUsers.length > 0) {
        const transformedActivity: Activity[] = activityData.topUsers.map((userItem: any, index: number) => ({
          id: userItem.userId || `user-activity-${index}`,
          query: `User ${userItem.userName} made ${userItem.queryCount} queries.`,
          user: userItem.userName,
          timestamp: 'Recently', // Placeholder
          status: 'success',
        }));
        setRecentActivity(transformedActivity);
      } else {
        setRecentActivity([]);
      }
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
      setErrorActivity('Failed to load recent activity. Please try again later.');
      setRecentActivity([]);
    } finally {
      setLoadingActivity(false);
    }

    try {
      // Fetch Quota Status
      const quotaStatusData = await api.getQuotaStatus();
      setQuotaData(quotaStatusData);
    } catch (error) {
      console.error('Failed to fetch quota status:', error);
      setQuotaData(null);
    } finally {
      setLoadingQuota(false);
    }

    setLastRefreshed(new Date()); // Update last refreshed timestamp
  }, []); // No dependencies, as it uses state setters and api which are stable

  // Initial data fetch on component mount
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]); // Dependency on fetchDashboardData to ensure it runs when the function is stable

  // Auto-refresh effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    const REFRESH_INTERVAL = 60 * 1000; // 60 seconds

    const startRefresh = () => {
      if (autoRefresh && !document.hidden) {
        intervalId = setInterval(() => {
          fetchDashboardData();
        }, REFRESH_INTERVAL);
      }
    };

    const stopRefresh = () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };

    // Start refresh when component mounts or autoRefresh is enabled
    startRefresh();

    // Listen for visibility changes to pause/resume refresh
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopRefresh();
      } else {
        startRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on component unmount or when autoRefresh changes
    return () => {
      stopRefresh();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoRefresh, fetchDashboardData]); // Re-run effect if autoRefresh or fetchDashboardData changes

  const quickActions: QuickAction[] = [
    { label: 'Upload New Document', onClick: () => navigate('/documents'), variant: 'primary' },
    { label: 'View Analytics', onClick: () => navigate('/analytics'), variant: 'secondary' },
    { label: 'Manage Users', onClick: () => navigate('/users'), variant: 'secondary' },
    { label: 'System Health', onClick: () => setIsHealthModalOpen(true), variant: 'success' },
    // { label: 'Chat wit AI', onClick: () => isChatModalOpen ? setIsChatModalOpen(false) : setIsChatModalOpen(true), variant: 'chat' },
  ];

  const getStatusBadgeColor = (status: string): string => {
    switch (status) {
      case 'success': return 'bg-success/20 text-success';
      case 'failed': return 'bg-error/20 text-error';
      case 'processing': return 'bg-warning/20 text-warning';
      default: return 'bg-gray-200 text-gray-700';
    }
  };

  // Define the stats array using the fetched dashboardStats
  const stats: Stat[] = [
    { id: 1, name: 'Documents', stat: loadingStats ? <LoadingSpinner size="sm" /> : dashboardStats.totalDocuments, icon: FileText, color: 'text-primary-500' },
    { id: 2, name: 'Queries', stat: loadingStats ? <LoadingSpinner size="sm" /> : dashboardStats.queriesThisMonth, icon: MessageSquare, color: 'text-secondary-500' },
    { id: 3, name: 'Engaging Users', stat: loadingStats ? <LoadingSpinner size="sm" /> : dashboardStats.activeUsers, icon: Users, color: 'text-success' },
    { id: 4, name: 'Avg Response Time', stat: loadingStats ? <LoadingSpinner size="sm" /> : dashboardStats.avgResponseTime, icon: TrendingUp, color: 'text-warning' },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Dashboard Header with Title and Auto-refresh Controls */}
      {/* Modified for responsive layout: stacks on mobile, side-by-side on larger screens */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-4 sm:mb-0">Welcome, {user?.name || 'Admin'}!</h2>
        {/* Added items-end for right alignment on mobile, sm:items-center for center alignment on larger screens */}
        <div className="flex flex-col items-end sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
          {/* Auto-refresh toggle */}
          <div className="flex items-center space-x-2">
            <ToggleSwitch
              id="auto-refresh"
              label="Auto-refresh"
              checked={autoRefresh}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAutoRefresh(e.target.checked)}
            />
            {/*

              isOn={autoRefresh} handleToggle={() => setAutoRefresh(!autoRefresh)}*/}

            {/* Manual Refresh Button */}
            <Button
              onClick={fetchDashboardData}
              variant="secondary"
              className="flex items-center space-x-2"
              disabled={loadingStats || loadingActivity}
            >
              <RefreshCw className={`w-4 h-4 ${loadingStats || loadingActivity ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </Button>
          </div>
          {/* Last updated display - moved here for responsive stacking */}
          <div className="text-sm px-3 text-gray-500 md:order-first">
            {lastRefreshed ? `Last refreshed: ${lastRefreshed.toLocaleString()}` : 'Loading...'}
          </div>
        </div>
      </div>

      {/* Quick Stats Cards */}
      <h3 className="text-xl font-semibold text-gray-900 mb-4">Monthly Summary</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((item) => (
          <Card key={item.id} className="flex items-center space-x-4 p-5">
            <div className={`p-3 rounded-full ${item.color.replace('text-', 'bg-')}/10`}>
              <item.icon className={`w-6 h-6 ${item.color}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">{item.name}</p>
              {errorStats ? (
                <p className="text-error text-xl font-semibold">{item.stat}</p>
              ) : (
                <p className="text-2xl font-semibold text-gray-900">{item.stat}</p>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Quota Usage */}
      <h3 className="text-xl font-semibold text-gray-900 mb-4">Resource Quotas & Usage</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <QuotaCard quotaData={quotaData} loading={loadingQuota} />
      </div>

      {/* Quick Actions */}
      <Card>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-4">
          {quickActions.map((action, index) => (
            <Button key={index} onClick={action.onClick} variant={action.variant}>
              {action.label}
            </Button>
          ))}
        </div>
      </Card>

      {/* Recent Activity */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900">Recent Activity</h3>
          <Button variant="ghost" size="sm" onClick={() => navigate('/analytics')}>View All</Button> {/* Link to Analytics page */}
        </div>
        {loadingActivity ? (
          <div className="flex justify-center items-center h-24">
            <LoadingSpinner />
          </div>
        ) : errorActivity ? (
          <p className="text-error text-center py-4">{errorActivity}</p>
        ) : recentActivity.length === 0 ? (
          <p className="text-gray-600 text-center py-4">No recent activity found.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {recentActivity.map((activity) => (
              <li key={activity.id} className="py-4 flex flex-col sm:flex-row sm:items-center justify-between">
                <div className="flex-1 mb-2 sm:mb-0">
                  <p className="text-gray-800 font-medium">{activity.query}</p>
                  <p className="text-sm text-gray-500">{activity.user} • {activity.timestamp}</p>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(activity.status)}`}>
                  {activity.status.charAt(0).toUpperCase() + activity.status.slice(1)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {
        isHealthModalOpen && (
          <SystemHealthModal
            isOpen={isHealthModalOpen}
            onClose={() => setIsHealthModalOpen(false)}
          />
        )
      }



      {/* SmartDocs AI chat Integration */}
      {/* --- START OF MODIFICATION ---
      {
        isChatModalOpen && (
           <ChatModal isOpen={isChatModalOpen}
                  onClose={() => setIsChatModalOpen(false)}
                  title="SmartDocs AI Chat">
              <ChatWindow />
          </ChatModal>
        )
      }
      {/* --- END OF MODIFICATION --- */}
    </div>
  );
};

export default Dashboard;
